import React, { memo, useCallback, useState } from 'react';
import type { StepNode } from '@segmently/schema-models/v2';
import type { OnActionCallback, OnEventCallback } from '../../../types/events';
import type { TemplateVariables } from '../../../utils/contentResolver';
import { resolveLabel } from '../../../utils/contentResolver';
import type { PreviewMode } from '../../../types/previewMode';
import { createFieldChangeEvent } from '../../../utils/eventEmitter';
import { getButtonStateFromSchema } from '../../../utils/buttonVisibility';
import { validateFieldValue } from '../../shared/Field/Field.utils';

import { CopyBlock } from '../../shared/CopyBlock';
import { Field } from '../../shared/Field';
import { ActionBar } from '../../shared/ActionBar';
import { Background } from '../../shared/Background';
import { HeaderBar } from '../../shared/HeaderBar';
import styles from './TextInput.module.css';

export interface TextInputProps {
  step: StepNode;
  locale: string;
  variables?: TemplateVariables;
  resolvedScreenValues?: Record<string, string>;
  onAction?: OnActionCallback;
  onEvent?: OnEventCallback;
  editMode?: boolean;
  previewMode?: PreviewMode;
  fieldValue?: string;
  onFieldValueChange?: (value: string) => void;
}

export const TextInput = memo(function TextInput({
  step,
  locale,
  variables,
  resolvedScreenValues: _resolvedScreenValues,
  onAction,
  onEvent,
  editMode = false,
  previewMode,
  fieldValue = '',
  onFieldValueChange,
}: TextInputProps): React.ReactElement {
  const { content } = step;

  const [fieldError, setFieldError] = useState<string | null>(null);

  const fieldType = content?.textField?.fieldSubtype || content?.textField?.fieldType;
  const fieldValidation = content?.textField?.validationRegex;
  const fieldMin = content?.textField?.min;
  const fieldMax = content?.textField?.max;

  const hasValue = Boolean(fieldValue && fieldValue.trim());
  // D-W2-2: the gate used to be `isEmailField ? hasValidationError : undefined`, so a
  // field with an authored `validationRegex` (which W2's compiler now emits on numeric and
  // short-text screens) rendered its error message under a still-enabled CTA. `fieldError`
  // is only ever set by a validation that actually failed, whatever the field type — so
  // the flag is the flag, and the special case is gone.
  const hasValidationError = fieldError !== null;

  const primaryState = getButtonStateFromSchema(
    content?.actionBar?.primary,
    hasValue,
    hasValidationError
  );

  const secondaryState = getButtonStateFromSchema(
    content?.actionBar?.secondary,
    hasValue,
    hasValidationError
  );

  const handleFieldChange = useCallback((value: string) => {
    if (fieldError) {
      setFieldError(null);
    }

    onFieldValueChange?.(value);

    if (onEvent) {
      onEvent(createFieldChangeEvent(value, step, 'field'));
    }
  }, [step, onEvent, onFieldValueChange, fieldError]);

  const handleFieldBlur = useCallback(() => {
    if (!fieldValue || !fieldValue.trim()) {
      setFieldError(null);
      return;
    }

    // V2: validationRegex is a string, wrap for validateFieldValue's expected format.
    // W8(d): `min`/`max` ride along — the compiler materializes an inclusive
    // numeric range onto the field (D-W2-3), and the primary CTA is already
    // gated on `hasValidationError`, so honouring the range here is what makes
    // `0 kg` and `999 years` unsubmittable.
    const result = validateFieldValue(
      fieldValue,
      fieldType,
      (fieldValidation || fieldMin !== undefined || fieldMax !== undefined)
        ? {
          ...(fieldValidation ? { regex: fieldValidation } : {}),
          ...(fieldMin !== undefined ? { min: fieldMin } : {}),
          ...(fieldMax !== undefined ? { max: fieldMax } : {}),
        }
        : undefined
    );

    if (!result.isValid) {
      const schemaErrorMsg = resolveLabel(content?.textField?.errorMessage, locale);
      const errorMsg = schemaErrorMsg || result.error || 'Invalid input';
      setFieldError(errorMsg);

      onEvent?.(createFieldChangeEvent('field-validation-error', step, 'field'));
    } else {
      setFieldError(null);
    }
  }, [fieldValue, fieldType, fieldValidation, fieldMin, fieldMax, content?.textField?.errorMessage, locale, onEvent, step]);

  const handleFieldFocus = useCallback(() => {
    if (fieldError) {
      setFieldError(null);
    }
  }, [fieldError]);

  const ep = editMode || previewMode === 'live' ? 'content' : undefined;

  return (
    <div
      className={`${styles.container}${previewMode === 'live' ? ` ${styles.liveMode}` : ''}${editMode ? ' screen-view--edit-mode' : ''}`}
      data-testid="view-text-input"
    >

      <Background
        backdrop={content?.canvas?.background}
        locale={locale}
        editPath={ep ? `${ep}.canvas.background` : undefined}
        previewMode={previewMode}
      />

      <HeaderBar
        header={content?.header}
        locale={locale}
        variables={variables}
        step={step}
        onAction={onAction}
        editPath={ep ? `${ep}.header` : undefined}
      />

      <div className={`${styles.content} ${styles.xPaddings}`}>
        <div className={styles.header}>
          <CopyBlock
            copyBlock={content?.copy}
            locale={locale}
            variables={variables}
            editPath={ep ? `${ep}.copy` : undefined}
          />
        </div>

        <div className={styles.fieldContainer}>
          <Field
            textField={content?.textField}
            locale={locale}
            value={fieldValue}
            error={fieldError}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            onFocus={handleFieldFocus}
            editPath={ep ? `${ep}.textField` : undefined}
            previewMode={previewMode}
          />
        </div>
      </div>

      <ActionBar
        actionBar={content?.actionBar}
        locale={locale}
        variables={variables}
        step={step}
        onAction={onAction}
        primaryState={primaryState}
        secondaryState={secondaryState}
        editPath={ep ? `${ep}.actionBar` : undefined}
      />
    </div>
  );
});

TextInput.displayName = 'TextInput';
